import { createSelector } from "@reduxjs/toolkit";
import type {
  GetMcbimItemsInSpaceApiArg,
  GetMcbimItemsInSpaceApiResponse,
  PostMultiplacerDevicesApiArg,
  PostMultiplacerDevicesApiResponse,
} from "../../../../openapi";
import { openapi } from "../../../../openapi";
import { IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import { IRootState } from "../../../app/store";
import { buildCeilingGridPixPathsFromLines } from "../../../buildCeilingGridPixPathsFromLines";
import { convertDevicesToPixPolygons } from "../../../DesignTab/devices/deviceUtils";
import { selectHttpDevicesInRoom } from "../../../DesignTab/devices/deviceSlice";
import { selectCompCodePath, selectProjectNo } from "../../../globalSelectors";
import { selectActivePartialMcbimSpace } from "../../../McBim/mcbimSelectors";
import { PartialMcBimSpace } from "../../../utils";
import { selectHttpMcBimItemsInSelectedRoom } from "../../singleRoomSelectors";
import { formatMcBimItemsToPixPolygons } from "../../../utils";
import {
  selectSelectedSimilarRoomWithDevices,
  selectSimilarRoomsWithDevicesCurrentRoomName,
} from "./similarRoomsWithDevicesSelectors";

const EMPTY_DEVICES: PostMultiplacerDevicesApiResponse = [];
const EMPTY_MCBIM_ITEMS: GetMcbimItemsInSpaceApiResponse = [];

export const selectSimilarRoomWithDevicesViewerDevicesArgs = createSelector(
  [selectProjectNo, selectSelectedSimilarRoomWithDevices],
  (projectNo, room): PostMultiplacerDevicesApiArg | null => {
    if (!projectNo || !room) {
      return null;
    }

    return {
      body: {
        projectNo,
        roomNumbers: [room.roomNumber],
      },
    };
  }
);

export const selectSimilarRoomWithDevicesViewerMcbimItemsArgs = createSelector(
  [selectProjectNo, selectSelectedSimilarRoomWithDevices],
  (projectNo, room): GetMcbimItemsInSpaceApiArg | null => {
    if (!projectNo || !room) {
      return null;
    }

    return {
      projectNo,
      spaceName: room.roomNumber,
    };
  }
);

export const selectSimilarRoomWithDevicesSourceDevices = createSelector(
  [selectSimilarRoomWithDevicesViewerDevicesArgs, (state: IRootState) => state],
  (args, state): PostMultiplacerDevicesApiResponse => {
    if (!args) {
      return EMPTY_DEVICES;
    }

    return openapi.endpoints.postMultiplacerDevices.select(args)(state).data ?? EMPTY_DEVICES;
  }
);

const selectViewerMcbimItems = createSelector(
  [selectSimilarRoomWithDevicesViewerMcbimItemsArgs, (state: IRootState) => state],
  (args, state): GetMcbimItemsInSpaceApiResponse => {
    if (!args) {
      return EMPTY_MCBIM_ITEMS;
    }

    return openapi.endpoints.getMcbimItemsInSpace.select(args)(state).data ?? EMPTY_MCBIM_ITEMS;
  }
);

const selectSimilarRoomWithDevicesPixPolygons = createSelector(
  [selectSelectedSimilarRoomWithDevices, selectSimilarRoomsWithDevicesCurrentRoomName],
  (room, roomName): IPixPolygon[] => {
    if (!room) {
      return [];
    }

    return [
      {
        id: room.roomNumber,
        points: room.polygon as number[][],
        fillColor: "#ffffff",
        strokeColor: "#000000",
        strokeWidth: "10",
        text: {
          values: [room.roomNumber, roomName ?? "-"],
          color: "#000000",
          fontSize: "1",
          show: false,
        },
        isMoveable: false,
        tooltip: undefined,
        isDragSelectable: false,
      },
    ];
  }
);

const selectSimilarRoomWithDevicesFurniturePixPolygons = createSelector(
  [selectViewerMcbimItems, selectCompCodePath],
  (mcbimItems, compCodePath): IPixPolygon[] => {
    if (!compCodePath) {
      return [];
    }

    const furnitureItems = mcbimItems.filter((item) => item.instanceClass === "IfcFurniture");

    if (furnitureItems.length === 0) {
      return [];
    }

    return formatMcBimItemsToPixPolygons({
      mcBimItems: furnitureItems,
      compCodePath,
      showDuctSegment: false,
    });
  }
);

const selectSimilarRoomWithDevicesDevicePixPolygons = createSelector(
  [selectSimilarRoomWithDevicesSourceDevices],
  (devices): IPixPolygon[] =>
    convertDevicesToPixPolygons({
      devices,
      typeDevices: [],
      selectedDeviceId: null,
      hideDevices: false,
      showTrueSize: false,
      isMoveable: false,
    })
);

export const selectSimilarRoomWithDevicesViewerPixPolygons = createSelector(
  [
    selectSimilarRoomWithDevicesPixPolygons,
    selectSimilarRoomWithDevicesFurniturePixPolygons,
    selectSimilarRoomWithDevicesDevicePixPolygons,
  ],
  (roomPolygons, furniturePolygons, devicePolygons): IPixPolygon[] => [
    ...roomPolygons,
    ...furniturePolygons,
    ...devicePolygons,
  ]
);

export const selectSimilarRoomWithDevicesViewerCeilingGridPixPaths = createSelector(
  [selectSelectedSimilarRoomWithDevices],
  (room) =>
    buildCeilingGridPixPathsFromLines(
      room
        ? [
            {
              name: room.roomNumber,
              globalId: room.roomNumber,
              buildingStory: null,
              fileName: "",
              polygon: room.polygon,
              ceilingGrids: room.ceilingGrids,
            },
          ]
        : []
    )
);

export const selectSimilarRoomWithDevicesViewerZoomExtentKey = createSelector(
  [
    selectSelectedSimilarRoomWithDevices,
    selectSimilarRoomWithDevicesSourceDevices,
    selectSimilarRoomWithDevicesFurniturePixPolygons,
    selectSimilarRoomWithDevicesViewerCeilingGridPixPaths,
  ],
  (room, devices, furniturePolygons, ceilingGridPaths): string =>
    room
      ? `similar-room-with-devices-${room.roomNumber}-${devices.length}-${furniturePolygons.length}-${ceilingGridPaths.length}`
      : "similar-room-with-devices-empty"
);

export const selectSelectedSimilarRoomWithDevicesAsMcbimSpace = createSelector(
  [selectSelectedSimilarRoomWithDevices, selectSimilarRoomsWithDevicesCurrentRoomName],
  (room, roomName): PartialMcBimSpace | null => {
    if (!room) {
      return null;
    }

    return {
      name: room.roomNumber,
      longName: roomName,
      polygon: room.polygon,
      ceilingGrids: room.ceilingGrids,
      buildingStory: room.floor
        ? {
            globalId: room.floor,
            name: room.floor,
          }
        : null,
    } as PartialMcBimSpace;
  }
);

export const selectCopySimilarRoomWithDevicesToCurrentRoomState = createSelector(
  [
    selectSelectedSimilarRoomWithDevicesAsMcbimSpace,
    selectActivePartialMcbimSpace,
    selectSimilarRoomWithDevicesSourceDevices,
    selectHttpDevicesInRoom,
    (state: IRootState) => state.design.nonPublischedDevicesFromAlgos,
  ],
  (sourceSpace, targetSpace, sourceDevices, targetDevices, nonPublishedDevices) => {
    if (!sourceSpace || !targetSpace) {
      return {
        disabled: true,
        showCopyButton: false,
        hint: "Velg et rom å kopiere fra",
      };
    }

    if (sourceSpace.name === targetSpace.name) {
      return {
        disabled: true,
        showCopyButton: false,
        hint: "Samme rom kan ikke kopieres",
      };
    }

    if (sourceDevices.length === 0) {
      return {
        disabled: true,
        showCopyButton: true,
        hint: "Laster komponenter fra valgt rom",
      };
    }

    if (targetDevices.length > 0) {
      return {
        disabled: true,
        showCopyButton: false,
        hint: "Aktuelt rom har allerede komponenter",
      };
    }

    const existingPreviewDevices = nonPublishedDevices.filter(
      (device) => device.roomNumber === targetSpace.name
    );

    if (existingPreviewDevices.length > 0) {
      return {
        disabled: true,
        showCopyButton: false,
        hint: "Aktuelt rom har allerede forhåndskopierte komponenter",
      };
    }

    return {
      disabled: false,
      showCopyButton: true,
      hint: "Klar til å kopiere",
    };
  }
);

const selectCurrentRoomPixPolygons = createSelector(
  [selectActivePartialMcbimSpace, selectSimilarRoomsWithDevicesCurrentRoomName],
  (space, roomName): IPixPolygon[] => {
    if (!space?.polygon || !space.name) {
      return [];
    }

    return [
      {
        id: space.name,
        points: space.polygon as number[][],
        fillColor: "#ffffff",
        strokeColor: "#000000",
        strokeWidth: "10",
        text: {
          values: [space.name, roomName ?? "-"],
          color: "#000000",
          fontSize: "1",
          show: false,
        },
        isMoveable: false,
        tooltip: undefined,
        isDragSelectable: false,
      },
    ];
  }
);

const selectCurrentRoomFurniturePixPolygons = createSelector(
  [selectHttpMcBimItemsInSelectedRoom, selectCompCodePath],
  (mcbimItems, compCodePath): IPixPolygon[] => {
    if (!compCodePath) {
      return [];
    }

    const furnitureItems = mcbimItems.filter((item) => item.instanceClass === "IfcFurniture");
    if (furnitureItems.length === 0) {
      return [];
    }

    return formatMcBimItemsToPixPolygons({
      mcBimItems: furnitureItems,
      compCodePath,
      showDuctSegment: false,
    });
  }
);

const selectCurrentRoomDevicePixPolygons = createSelector(
  [
    selectHttpDevicesInRoom,
    selectActivePartialMcbimSpace,
    (state: IRootState) => state.design.nonPublischedDevicesFromAlgos,
  ],
  (savedDevices, activeSpace, nonPublishedDevices): IPixPolygon[] => {
    const previewDevices = activeSpace?.name
      ? nonPublishedDevices.filter((device) => device.roomNumber === activeSpace.name)
      : [];

    const devices = [...savedDevices, ...previewDevices];

    return convertDevicesToPixPolygons({
      devices,
      typeDevices: [],
      selectedDeviceId: null,
      hideDevices: false,
      showTrueSize: false,
      isMoveable: false,
    });
  }
);

const selectCurrentRoomDeviceCountForZoom = createSelector(
  [
    selectHttpDevicesInRoom,
    selectActivePartialMcbimSpace,
    (state: IRootState) => state.design.nonPublischedDevicesFromAlgos,
  ],
  (savedDevices, activeSpace, nonPublishedDevices): number => {
    const previewDeviceCount = activeSpace?.name
      ? nonPublishedDevices.filter((device) => device.roomNumber === activeSpace.name).length
      : 0;

    return savedDevices.length + previewDeviceCount;
  }
);

export const selectCurrentRoomComparisonViewerPixPolygons = createSelector(
  [selectCurrentRoomPixPolygons, selectCurrentRoomFurniturePixPolygons, selectCurrentRoomDevicePixPolygons],
  (roomPolygons, furniturePolygons, devicePolygons): IPixPolygon[] => [
    ...roomPolygons,
    ...furniturePolygons,
    ...devicePolygons,
  ]
);

export const selectCurrentRoomComparisonViewerCeilingGridPixPaths = createSelector(
  [selectActivePartialMcbimSpace],
  (space) => buildCeilingGridPixPathsFromLines(space ? [space] : [])
);

export const selectCurrentRoomComparisonViewerZoomExtentKey = createSelector(
  [
    selectActivePartialMcbimSpace,
    selectCurrentRoomDeviceCountForZoom,
    selectCurrentRoomFurniturePixPolygons,
    selectCurrentRoomComparisonViewerCeilingGridPixPaths,
  ],
  (space, deviceCount, furniturePolygons, ceilingGridPaths): string =>
    space?.name
      ? `current-room-comparison-${space.name}-${deviceCount}-${furniturePolygons.length}-${ceilingGridPaths.length}`
      : "current-room-comparison-empty"
);
